import {
  CANDIDATE_FIELDS,
  COMPANY_FIELDS,
  DEAL_FIELDS,
  FIELD_TYPE_CATALOGUE,
  JOB_FIELDS,
  PROJECT_FIELDS,
  type FieldFormat,
  type FieldType,
  type FilterCondition,
} from '@crm/contracts';
import type { FilterField } from '../components/record-table/filter-bar.tsx';
import type { FieldDefinitionView, MemberView, PipelineView } from './client.ts';
import { countryOptions } from './countries.ts';
import { BILLING_LABEL, memberOptions, stageOptions } from './record-data.ts';
import { HOURLY_RATE_FORMAT } from './value-tones.ts';

/**
 * The lists' filter bars (Candidates, Projects, Deals, Jobs): each field's type comes from the
 * contracts catalogue, the list only picks fields, labels and which are chips. Indexed custom
 * fields ("Filterable and sortable") are appended; the caller passes definitions already
 * narrowed to what the viewer's role may see. Free-text fields suggest values from the list's
 * own search.
 */
export type HintSearch = (body: {
  filter: FilterCondition;
  limit: number;
}) => Promise<{ items: readonly unknown[] }>;

interface Common {
  definitions: readonly FieldDefinitionView[];
  search: HintSearch;
}

type Options = FilterField['options'];
interface Pick<N> {
  name: N;
  label: string;
  primary?: boolean;
  options?: Options;
  format?: FieldFormat;
}

const HINT_LIMIT = 20;

export function hintsFrom(search: HintSearch, name: string) {
  const key = name.startsWith('custom.') ? name.slice('custom.'.length) : null;
  return async (text: string): Promise<string[]> => {
    const { items } = await search({
      filter: { field: name, op: 'contains', value: text },
      limit: 50,
    });
    const seen = new Map<string, string>();
    for (const item of items) {
      const row = item as Record<string, unknown>;
      const v = key ? (row.custom as Record<string, unknown> | undefined)?.[key] : row[name];
      const s = typeof v === 'string' ? v.trim() : '';
      if (s && !seen.has(s.toLowerCase())) seen.set(s.toLowerCase(), s);
    }
    return [...seen.values()].sort((a, b) => a.localeCompare(b)).slice(0, HINT_LIMIT);
  };
}

const withHints = (f: FilterField, search: HintSearch): FilterField =>
  !f.options?.length && FIELD_TYPE_CATALOGUE[f.type].operators.includes('contains')
    ? { ...f, hints: hintsFrom(search, f.name) }
    : f;

/** Whether the quick filters can edit the type: options to pick, text to match, a range, yes/no. */
function hasPanel(type: FieldType, options: Options): boolean {
  const ops = FIELD_TYPE_CATALOGUE[type].operators;
  if (options?.length) return ops.includes('in');
  return ops.includes('contains') || ops.includes('gte') || type === 'boolean';
}

function build<C extends Record<string, FieldType>>(
  catalogue: C,
  picks: ReadonlyArray<Pick<keyof C & string>>,
  { definitions, search }: Common,
): FilterField[] {
  const standard = picks.map(({ name, label, primary, options, format }): FilterField => {
    const type = catalogue[name] as FieldType;
    // An id column with a fixed set of choices filters as a dropdown.
    const asSelect = options && (type === 'reference' || type === 'user');
    return {
      name,
      label,
      type: asSelect ? 'single_select' : type,
      options,
      primary,
      ...(format ? { format } : {}),
    };
  });
  const custom = definitions
    .filter((d) => d.isIndexed && hasPanel(d.type, d.options))
    .map((d): FilterField => ({
      name: `custom.${d.key}`,
      label: d.label,
      type: d.type,
      options: d.options,
      ...(d.format ? { format: d.format } : {}),
    }));
  return [...standard, ...custom].map((f) => withHints(f, search));
}

export const candidateFilterFields = ({
  pipeline,
  ...c
}: Common & { pipeline: PipelineView | undefined }) =>
  build(
    CANDIDATE_FIELDS,
    [
      // The Candidate Flow's stages by name (the column holds the name), grouped by outcome.
      {
        name: 'stage',
        label: 'Stage',
        primary: true,
        options: stageOptions(pipeline).map((o) => ({ ...o, value: o.label })),
      },
      { name: 'country', label: 'Country', primary: true, options: countryOptions() },
      { name: 'expectedHourlyRate', label: 'Hourly Rate', format: HOURLY_RATE_FORMAT },
      { name: 'applicationDate', label: 'Applied' },
      { name: 'isBlocked', label: 'Blocked' },
    ],
    c,
  );

export const projectFilterFields = ({
  pipeline,
  members,
  ...c
}: Common & { pipeline: PipelineView | undefined; members: MemberView[] }) =>
  build(
    PROJECT_FIELDS,
    [
      {
        name: 'stageId',
        label: pipeline?.name ?? 'Stage',
        primary: true,
        options: stageOptions(pipeline),
      },
      { name: 'ownerId', label: 'Owner', primary: true, options: memberOptions(members) },
      { name: 'createdAt', label: 'Created' },
    ],
    c,
  );

export const dealFilterFields = ({
  pipeline,
  ...c
}: Common & { pipeline: PipelineView | undefined }) =>
  build(
    DEAL_FIELDS,
    [
      { name: 'stageId', label: 'Deal Flow', primary: true, options: stageOptions(pipeline) },
      {
        name: 'billingModel',
        label: 'Billing',
        primary: true,
        options: Object.entries(BILLING_LABEL).map(([value, label]) => ({ value, label })),
      },
      { name: 'value', label: 'Value' },
      { name: 'expectedCloseDate', label: 'Expected close' },
      { name: 'paidAt', label: 'Paid' },
    ],
    c,
  );

export const jobFilterFields = ({
  pipeline,
  members,
  ...c
}: Common & { pipeline: PipelineView | undefined; members: MemberView[] }) =>
  build(
    JOB_FIELDS,
    [
      { name: 'stageId', label: 'Job Flow', primary: true, options: stageOptions(pipeline) },
      { name: 'recruiterId', label: 'Recruiter', primary: true, options: memberOptions(members) },
      { name: 'interviewDateTime', label: 'Interview' },
    ],
    c,
  );

export const companyFilterFields = ({ members, ...c }: Common & { members: MemberView[] }) =>
  build(
    COMPANY_FIELDS,
    [
      { name: 'country', label: 'Country', primary: true, options: countryOptions() },
      { name: 'ownerId', label: 'Owner', primary: true, options: memberOptions(members) },
      { name: 'industry', label: 'Industry' },
      { name: 'createdAt', label: 'Created' },
    ],
    c,
  );
